import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import AppHeaderLayout from '../components/AppHeaderLayout';
import Spinner from '../components/Spinner';
import { getNextSandboxPostcard, approveImageRelation, rejectImageRelation } from '../api/image_relations';
import { useTranslation } from 'react-i18next';
import { iconButtonStyles } from '../styles/commonStyles';
import { BiMenu } from 'react-icons/bi';
import { FaThumbsUp, FaThumbsDown } from 'react-icons/fa';

function Sandbox() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);
	const [currentPostcard, setCurrentPostcard] = useState(null);
	const [isVoting, setIsVoting] = useState(false);
	
	// Получаем ID пользователя из Redux состояния
	const user = useSelector((state) => state.auth.user);
	const telegramUser = useSelector((state) => state.telegram.telegramUser);
	const userId = user?.id || telegramUser?.id;

	// Получаем следующую открытку для модерации
	const { data: nextPostcardData, isLoading: loadingPostcard, refetch: refetchPostcard } = useQuery(
		['nextSandboxPostcard', userId],
		() => getNextSandboxPostcard(userId),
		{ 
			enabled: !!userId,
			onSuccess: (data) => {
				if (data.success) {
					setCurrentPostcard(data.postcard);
				} else {
					setCurrentPostcard(null);
				}
			}
		}
	);

	// Обработчик одобрения открытки
	const handleApprove = async () => {
		if (!currentPostcard || isVoting) return;
		
		setIsVoting(true);
		try {
			const response = await approveImageRelation(currentPostcard.id, userId);
			if (response.success) {
				// Получаем следующую открытку
				await refetchPostcard();
			}
		} catch (error) {
			console.error('Ошибка при одобрении:', error);
		} finally {
			setIsVoting(false);
		}
	};

	// Обработчик отклонения открытки
	const handleReject = async () => {
		if (!currentPostcard || isVoting) return;
		
		setIsVoting(true);
		try {
			const response = await rejectImageRelation(currentPostcard.id, userId);
			if (response.success) {
				// Получаем следующую открытку
				await refetchPostcard();
			}
		} catch (error) {
			console.error('Ошибка при отклонении:', error);
		} finally {
			setIsVoting(false);
		}
	};

	const leftContent = (
		<div className="flex items-center gap-2">
			<button
				className={iconButtonStyles + " block sm:hidden mr-2"}
				onClick={() => setSidebarMenuOpen(true)}
				aria-label="Открыть меню"
			>
				<BiMenu className="text-xl" />
			</button>
			<span className="font-semibold text-base">{t('tabs.sandbox', 'Песочница')}</span>
		</div>
	);

	// Если нет пользователя
	if (!userId) {
		return (
			<AppHeaderLayout
				leftContent={leftContent}
				sidebarMenuOpen={sidebarMenuOpen}
				onSidebarMenuOpen={() => setSidebarMenuOpen(true)}
				onSidebarMenuClose={() => setSidebarMenuOpen(false)}
				title={t('tabs.sandbox', 'Песочница')}
			>
				<div className="p-4 text-center text-gray-500 dark:text-gray-400">
					Необходимо авторизоваться для доступа к песочнице
				</div>
			</AppHeaderLayout>
		);
	}

	return (
		<AppHeaderLayout
			leftContent={leftContent}
			sidebarMenuOpen={sidebarMenuOpen}
			onSidebarMenuOpen={() => setSidebarMenuOpen(true)}
			onSidebarMenuClose={() => setSidebarMenuOpen(false)}
			title={t('tabs.sandbox', 'Песочница')}
		>
			<div className="p-4">
				{loadingPostcard ? (
					<div className="flex justify-center items-center min-h-[400px]">
						<Spinner />
					</div>
				) : !currentPostcard ? (
					<div className="flex justify-center items-center min-h-[400px]">
						<div className="text-center text-gray-500 dark:text-gray-400">
							<p className="text-lg mb-2">🎉</p>
							<p className="text-xl font-medium mb-2">
								{nextPostcardData?.message || 'Все открытки в песочнице проголосованы вами!'}
							</p>
							<p className="text-sm">
								Зайдите позже для голосования за новые открытки
							</p>
						</div>
					</div>
				) : (
					<div className="max-w-2xl mx-auto">
						{/* Карточка открытки */}
						<div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-6">
							{/* Изображение */}
							<div className="relative">
								{currentPostcard.image_url?.endsWith('.mp4') ? (
									<video
										src={currentPostcard.image_url}
										className="w-full max-h-[70vh] object-contain"
										controls
										autoPlay
										muted
										loop
									/>
								) : (
									<img
										src={currentPostcard.image_url}
										alt={currentPostcard.file_name || 'Открытка'}
										className="w-full max-h-[70vh] object-contain"
									/>
								)}
							</div>
							
							{/* Информация об открытке */}
							<div className="p-6">
								<h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
									{currentPostcard.file_name || 'Без названия'}
								</h3>
								
								{currentPostcard.description && (
									<p className="text-gray-600 dark:text-gray-300 mb-4">
										{currentPostcard.description}
									</p>
								)}
								
								{currentPostcard.created_at && (
									<div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
										<span>Создано: {new Date(currentPostcard.created_at).toLocaleDateString()}</span>
									</div>
								)}
								
								{/* Кнопки голосования */}
								<div className="flex gap-4">
									<button
										onClick={handleApprove}
										disabled={isVoting}
										className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
									>
										<FaThumbsUp />
										Одобрить
									</button>
									
									<button
										onClick={handleReject}
										disabled={isVoting}
										className="flex-1 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
									>
										<FaThumbsDown />
										Отклонить
									</button>
								</div>
							</div>
						</div>
						
						{/* Информация о процессе */}
						<div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
							<p className="mb-2 font-medium">Голосуйте за открытку, которую считаете достойной каталога.</p>
							<p className="mb-2">Действуйте по принципу "Нравится" / "Не нравится".</p>
							<p>Также отклоняйте работы, если они запрещены законом, 18+ контент или не является открыткой или поздравлением, не несёт позитивного настроя.</p>
						</div>
					</div>
				)}
			</div>
		</AppHeaderLayout>
	);
}

export default Sandbox;